import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Lightbulb, RefreshCw, Check, X, Loader2, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const URGENCY_STYLES = {
  high: 'border-rose-300 bg-rose-50',
  medium: 'border-amber-300 bg-amber-50',
  low: 'border-sky-300 bg-sky-50',
};

const URGENCY_BADGE = {
  high: 'bg-rose-500 text-white',
  medium: 'bg-amber-500 text-white',
  low: 'bg-sky-500 text-white',
};

export default function SuggestedAutomationsPanel({ onAccept }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [signals, setSignals] = useState({});
  const [cached, setCached] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/suggest-automations/scan${force ? '?force=true' : ''}`);
      setSuggestions(data.suggestions || []);
      setSignals(data.signals || {});
      setCached(!!data.cached);
    } catch (e) {
      // Silent — this is a nice-to-have; don't spam toasts on nav
      // toast.error('Could not load suggestions');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const accept = async (sid) => {
    try {
      await axios.post(`${API}/api/suggest-automations/${sid}/accept`);
      toast.success('Draft automation created — review it in the list below');
      setSuggestions(prev => prev.filter(s => s.id !== sid));
      onAccept?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not accept suggestion');
    }
  };

  const dismiss = async (sid) => {
    try {
      await axios.post(`${API}/api/suggest-automations/${sid}/dismiss`, { reason: 'user_dismissed' });
      setSuggestions(prev => prev.filter(s => s.id !== sid));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not dismiss');
    }
  };

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (!loading && suggestions.length === 0) {
    // Compact empty state
    return (
      <Card className="border-dashed" data-testid="suggested-automations-empty">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <Lightbulb className="h-4 w-4 text-amber-500" />
            <span>No suggestions right now — your platform looks healthy. Re-scan anytime to check for new pain signals.</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => load(true)} disabled={loading} data-testid="suggested-automations-rescan">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1" /> Re-scan</>}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="suggested-automations-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            Suggested Automations
            {cached && <Badge variant="outline" className="text-[10px] text-gray-500">cached · 6h TTL</Badge>}
          </CardTitle>
          <div className="flex items-center gap-2">
            {Object.keys(signals).length > 0 && (
              <span className="text-xs text-gray-500">
                Based on {Object.keys(signals).length} pain signal{Object.keys(signals).length === 1 ? '' : 's'} in the last 30d
              </span>
            )}
            <Button variant="outline" size="sm" onClick={() => load(true)} disabled={loading} data-testid="suggested-automations-rescan-btn">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1" /> Re-scan</>}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && suggestions.length === 0 && (
          <div className="text-sm text-gray-500 py-4 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Analyzing your platform signals…
          </div>
        )}
        {suggestions.map((s) => (
          <div key={s.id} className={`p-3 rounded-lg border ${URGENCY_STYLES[s.urgency] || URGENCY_STYLES.medium}`} data-testid={`suggestion-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`${URGENCY_BADGE[s.urgency] || URGENCY_BADGE.medium} text-[10px] uppercase`}>{s.urgency || 'medium'}</Badge>
                  <div className="text-sm font-semibold text-gray-900">{s.title}</div>
                </div>
                <p className="text-xs text-gray-600 mt-1">{s.why}</p>
                {s.expected_impact && (
                  <p className="text-xs text-emerald-700 mt-1 font-medium">Expected: {s.expected_impact}</p>
                )}
                {expanded[s.id] && s.automation && (
                  <div className="mt-2 p-2 bg-white rounded border text-[11px] text-gray-700 font-mono overflow-x-auto">
                    <pre className="whitespace-pre-wrap">{JSON.stringify(s.automation, null, 2)}</pre>
                  </div>
                )}
                <button
                  onClick={() => toggle(s.id)}
                  className="text-[11px] text-gray-500 hover:text-gray-700 mt-1 underline"
                  data-testid={`suggestion-toggle-${s.id}`}
                >
                  {expanded[s.id] ? 'Hide details' : 'Show automation JSON'}
                </button>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" onClick={() => accept(s.id)} data-testid={`suggestion-accept-${s.id}`}>
                  <Check className="h-3.5 w-3.5 mr-1" /> Accept
                </Button>
                <Button size="sm" variant="ghost" className="h-8 text-xs text-gray-500 hover:text-red-600" onClick={() => dismiss(s.id)} data-testid={`suggestion-dismiss-${s.id}`}>
                  <X className="h-3.5 w-3.5 mr-1" /> Dismiss
                </Button>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
